import { Dispatch, SetStateAction, useMemo, type ChangeEvent } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  ArrowUpRight as ArrowUpRightIcon,
  DataImportHub as DataImportHubIcon,
  Folder,
} from '@mint-lib/icons';
import {
  Button,
  Divider,
  FeaturedIcon,
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';
import { ProgressCircular, SearchBar } from '@mint-lib/tw-components/patterns';

import { useKnowledgeBase } from '../../../hooks/useKnowledgeBase.js';
import type {
  FileNode,
  FileSystemNode,
  FolderCrumb,
  FolderNode,
} from '../../../types/fileSystem.js';
import { FileSystemBreadcrumbs } from '../../KnowledgeBase/FileSystem/Breadcrumbs/Breadcrumbs.jsx';
import {
  CheckboxGroup,
  type CheckboxItem,
} from '../FileSelection/CheckboxGroup.jsx';

type Props = {
  isDisabled: boolean;
  isOpen: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  files: Array<FileNode & CheckboxItem>;
  folders: Array<FolderNode & CheckboxItem>;
  areFilesLoading: boolean;
  onItemChange: (
    label: string,
    checked: boolean,
    kind: FileSystemNode['kind'],
  ) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  breadcrumbItems: FolderCrumb[];
  onOpenFolder: (folder: FolderNode) => void;
  onCrumbNavigate: (id: string) => void;
  searchValue: string;
  handleChangeSearch: ({ target }: ChangeEvent) => void;
  handleClearSearch: () => void;
};

const DesktopNodeSelectionContent = ({
  isDisabled,
  isOpen,
  onOpenChange,
  areFilesLoading,
  onItemChange,
  onSelectAll,
  onDeselectAll,
  breadcrumbItems,
  onOpenFolder,
  onCrumbNavigate,
  files,
  folders,
  searchValue,
  handleChangeSearch,
  handleClearSearch,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const { toggle: toggleKnowledgeBase } = useKnowledgeBase();

  const nodes = useMemo(() => {
    return [...files, ...folders];
  }, [files, folders]);

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button icon variant="plain" disabled={isDisabled}>
          <DataImportHubIcon />
        </Button>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          className="z-popover flex max-h-100 w-122.5 flex-col gap-4"
          data-interactive
          side="bottom"
        >
          <div className="flex justify-between px-3 pt-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <DataImportHubIcon width={20} height={20} />
                <p className={typographyVariants({ variant: 'h4' })}>
                  {t('Knowledge base')}
                </p>
              </div>

              <div className="flex flex-col gap-0">
                <p
                  className={typographyVariants({
                    variant: 'bodyShort01',
                    color: 'secondary',
                  })}
                >
                  {t('By default, agents use all files in the Knowledge Base.')}
                </p>
                <p
                  className={typographyVariants({
                    variant: 'bodyShort01',
                    color: 'secondary',
                  })}
                >
                  {t('Select specific files or folders to limit the context.')}
                </p>
              </div>
            </div>

            <Button
              icon
              variant="text"
              size="large"
              onClick={toggleKnowledgeBase}
            >
              <ArrowUpRightIcon />
            </Button>
          </div>
          <div className="flex flex-col gap-3 px-3">
            <Divider />
            <SearchBar
              placeholder={t('Search a file or folder')}
              className="h-8 max-w-full"
              onChange={handleChangeSearch}
              onClear={handleClearSearch}
              value={searchValue}
            />
            <FileSystemBreadcrumbs
              items={breadcrumbItems}
              onFolderChange={onCrumbNavigate}
              gridApi={null}
            />
            {areFilesLoading ? (
              <div className="flex h-50 items-center justify-center">
                <ProgressCircular size="small" />
              </div>
            ) : (
              <div className="max-h-50 overflow-auto">
                {nodes.length ? (
                  <CheckboxGroup
                    items={nodes}
                    onItemChange={onItemChange}
                    onSelectAll={onSelectAll}
                    onDeselectAll={onDeselectAll}
                    onOpen={onOpenFolder}
                  />
                ) : (
                  <div className="flex h-50 w-full flex-col items-center justify-center gap-4 p-3">
                    <FeaturedIcon icon={<Folder />} />
                    <p
                      className={typographyVariants({
                        variant: 'h4',
                      })}
                    >
                      {t('This folder is empty')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};

export { DesktopNodeSelectionContent };
